import { Component, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AssetInstance } from '../../core/models/asset-instance.model';
import { CheckPoint } from '../../core/models/checkPoint.model';
import { Form } from '../../core/models/form.model';
import { AssetInstanceService } from '../../core/services/asset-instance.service';
import { BreadcrumbService } from '../../core/services/breadcrumb.service';
import { CheckPointService } from '../../core/services/checkPoint.service';
import { FormService } from '../../core/services/form.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../shared/components/card/card-title.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

@Component({
  selector: 'app-submission-success',
  imports: [
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    IconComponent,
    ButtonComponent,
    SpinnerComponent,
    TranslatePipe,
  ],
  templateUrl: './submission-success.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './submission-success.component.css',
})
export class SubmissionSuccessComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private checkPointService = inject(CheckPointService);
  private formService = inject(FormService);
  private assetInstanceService = inject(AssetInstanceService);
  private breadcrumbService = inject(BreadcrumbService);

  loading = signal(true);
  error = signal<string | null>(null);
  checkPoint = signal<CheckPoint | null>(null);
  form = signal<Form | null>(null);
  instance = signal<AssetInstance | null>(null);

  checkpointId!: string;
  formId!: string;
  instanceId: string | null = null;

  async ngOnInit() {
    this.checkpointId = this.route.snapshot.paramMap.get('checkpointId') ?? '';
    this.formId = this.route.snapshot.paramMap.get('formId') ?? '';
    this.instanceId = this.route.snapshot.paramMap.get('instanceId');
    await this.loadData();
  }

  private async loadData() {
    this.loading.set(true);
    this.error.set(null);

    try {
      const [checkPoint, form] = await Promise.all([
        this.checkPointService.getById(this.checkpointId),
        this.formService.getById(this.formId),
      ]);

      this.checkPoint.set(checkPoint);
      this.form.set(form);

      if (this.instanceId) {
        const instance = await this.assetInstanceService.getById(this.instanceId);
        this.instance.set(instance);
      }

      this.breadcrumbService.setDynamicLabels([checkPoint?.name ?? '', form?.name ?? '']);
    } catch {
      this.error.set('Failed to load submission details.');
    } finally {
      this.loading.set(false);
    }
  }

  fillAgain() {
    const segments = ['/submission', this.checkpointId, this.formId];
    if (this.instanceId) segments.push(this.instanceId);
    this.router.navigate(segments);
  }

  scanAnother() {
    this.router.navigate(['/scan']);
  }

  viewHistory() {
    const segments = ['/submission', this.checkpointId, this.formId];
    if (this.instanceId) segments.push(this.instanceId);
    segments.push('history');
    this.router.navigate(segments);
  }
}
